import type { ApiCheckout } from '../api/types'
import {
  CHECKOUT_SETTLE,
  PAYMENT_GATEWAY,
  PAYMENT_UI,
  type CheckoutSettleStatus,
  type PaymentCheckoutSession,
  type PaymentGatewayAdapter,
  type PaymentGatewayId,
} from '../constants/payments'
import { razorpayPaymentAdapter } from './razorpay-adapter'

export {
  isPaymentGateway,
  isRazorpayCheckoutPayload,
  readPaymentSession,
  sessionFromCheckout,
} from './payment-session'

export function paymentAdapterFor(
  gateway: PaymentGatewayId | null | undefined,
): PaymentGatewayAdapter {
  if (gateway === PAYMENT_GATEWAY.cashfree) {
    throw new Error(PAYMENT_UI.cashfreeUnavailable)
  }
  if (!gateway || gateway === PAYMENT_GATEWAY.razorpay) {
    return razorpayPaymentAdapter
  }
  throw new Error(PAYMENT_UI.gatewayRequired)
}

export async function payOnlineCheckout(input: {
  checkoutId: string
  session?: PaymentCheckoutSession | null
  gateway?: PaymentGatewayId | null
  timeoutMs?: number
}): Promise<{ status: CheckoutSettleStatus; checkout: ApiCheckout | null }> {
  if (input.timeoutMs !== undefined && input.timeoutMs <= 0) {
    return { status: CHECKOUT_SETTLE.expired, checkout: null }
  }
  const adapter = paymentAdapterFor(input.session?.gateway ?? input.gateway)
  const session = input.session ?? (await adapter.start(input.checkoutId))
  const collected = await adapter.collect(session, { timeoutMs: input.timeoutMs })
  if (collected.status === 'expired') {
    return { status: CHECKOUT_SETTLE.expired, checkout: null }
  }
  if (collected.status !== 'paid') {
    return { status: CHECKOUT_SETTLE.dismissed, checkout: null }
  }
  try {
    return {
      status: CHECKOUT_SETTLE.paid,
      checkout: (await adapter.verify(input.checkoutId, collected)) as ApiCheckout,
    }
  } catch {
    return { status: CHECKOUT_SETTLE.unverified, checkout: null }
  }
}
